import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { updateControlledInputValue } from '@ipfabric/frontend-shared-assets/utils/react/updateControlledInputValue';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import classNames from 'classnames';
import { FormInputButton } from 'components/FormInputButton.tsx/FormInputButton';
import type { RefObject } from 'react';
import {
  type ChangeEvent,
  type FC,
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { getSizeCssClass } from 'utils/form/getSizeCssClass';
import './TextInput.css';
import { isSafeNumber } from '@ipfabric/frontend-shared-assets/utils/types/isSafeNumber';
import { useAutoFocus } from '@ipfabric/ui-components/hooks/useAutoFocus';
import { isNullable } from '@ipfabric/utils';

interface PropsBase {
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  /**
   * TextInput requires onClear function so it can be decided if
   *  number is cleared to 0 or empty string etc.
   */
  onClear?: () => void;
  size?: 'normal' | 'small' | 'tiny';
  suffixText?: ReactNode;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FormEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent) => void;
  readOnly?: boolean;
  value: string;
  inputRef?: RefObject<HTMLInputElement>;
  name?: string;
  typeAhead?: boolean;
  loading?: boolean;
  /**
   * Render step-up and step-down buttons also for non-number inputs
   *
   * In this case, the buttons will be disabled if current input is not a valid number.
   */
  stepper?: boolean;
  dataTestId?: string;
}

export interface TextInputStringProps extends PropsBase {
  type: 'text' | 'password';
}

export interface TextInputNumberProps extends PropsBase {
  min?: number;
  max?: number;
  type: 'number';
}

export interface TextInputSearchProps extends PropsBase {
  type: 'search';
  variant?: 'normal' | 'underline';
}

const isNumberOutOfMinMaxRange = (
  value: number,
  min?: number,
  max?: number,
) => {
  return (!isNullable(min) && value < min) || (!isNullable(max) && value > max);
};

export const TextInput: FC<
  TextInputStringProps | TextInputNumberProps | TextInputSearchProps
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy component
> = (props) => {
  const inputRef = useAutoFocus(props.autoFocus);

  const [focused, setFocused] = useState<boolean>(false);
  const showSearchIcon = useMemo(() => {
    return !props.value && !focused;
  }, [focused, props.value]);

  const numberIsOutOfMinMaxRange =
    'min' in props && 'max' in props && props.value !== ''
      ? isNumberOutOfMinMaxRange(+props.value, props.min, props.max)
      : false;

  const numberIsOutOfRange =
    props.type === 'number' && !isSafeNumber(+props.value);

  const makeStep =
    (
      nativeStep: 'stepUp' | 'stepDown',
      nonNativeStep: (value: number) => number,
    ) =>
    (e: MouseEvent) => {
      e.preventDefault();

      if (props.type === 'number') {
        inputRef.current?.[nativeStep]();
        inputRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        if (inputRef.current) {
          updateControlledInputValue(
            inputRef.current,
            nonNativeStep(+inputRef.current.value).toString(),
          );
        }
      }
    };

  const stepUp = makeStep('stepUp', (x) => x + 1);
  const stepDown = makeStep('stepDown', (x) => x - 1);

  const canStep =
    props.type === 'number' ||
    props.value === '' || // support stepping for empty string considered as 0
    !(
      // https://stackoverflow.com/a/175787
      (
        Number.isNaN(+props.value) ||
        Number.isNaN(Number.parseFloat(props.value))
      )
    );

  return (
    <div
      className={classNames(
        'text-input-legacy',
        `text-input--${props.type}`,
        props.type === 'search' &&
          props.variant === 'underline' &&
          'text-input--underline',
        getSizeCssClass(props.size, 'text-input'),
        props.disabled && 'text-input--disabled',
        (props.hasError || numberIsOutOfMinMaxRange || numberIsOutOfRange) &&
          'text-input--error',
        props.typeAhead && 'text-input--inside-type-ahead',
        'overflow-hidden',
        props.className,
      )}
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      {props.type === 'search' && showSearchIcon && (
        <i className='fa-regular fa-magnifying-glass ml-2' />
      )}
      <input
        autoComplete={
          props.autoComplete ??
          (props.type === 'password' ? 'off' : props.autoComplete)
        }
        data-testid={props.dataTestId}
        disabled={props.disabled}
        value={props.value}
        onChange={props.onChange}
        type={props.type}
        placeholder={props.placeholder}
        ref={mergeRefs(props.inputRef, inputRef)}
        min={
          props.type === 'number'
            ? (props.min ?? Number.MIN_SAFE_INTEGER)
            : undefined
        }
        max={
          props.type === 'number'
            ? (props.max ?? Number.MAX_SAFE_INTEGER)
            : undefined
        }
        name={props.name}
        onFocus={(e) => {
          setFocused(true);
          return props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          return props.onBlur?.(e);
        }}
        onClick={props.onClick}
        onKeyDown={props.onKeyDown}
        readOnly={props.readOnly}
      />
      {(props.type === 'number' || props.stepper) && (
        <div className='text-input__stepper'>
          <button
            className='text-input__button'
            disabled={props.disabled || !canStep}
            onClick={stepUp}
            type='button'
          >
            <Icon glyph='fa-chevron-up' size='xs' />
          </button>
          <button
            className='text-input__button'
            disabled={props.disabled || !canStep}
            onClick={stepDown}
            type='button'
          >
            <Icon glyph='fa-chevron-down' size='xs' />
          </button>
        </div>
      )}
      {props.suffixText && (
        <div
          className={classNames(
            'text-input__suffix',
            props.type === 'number' && 'text-input__suffix--noborder',
          )}
        >
          {props.suffixText}
        </div>
      )}
      {props.loading && (
        <Icon className='fa-spin mr-3' glyph='fa-spinner' size='xs' />
      )}
      <div className='text-input__clear-button'>
        {props.onClear && props.value && (
          <FormInputButton
            className='mr-2'
            disabled={props.disabled}
            accent='negative'
            onClick={props.onClear}
          >
            <i className='fa-regular fa-xmark' />
          </FormInputButton>
        )}
      </div>
    </div>
  );
};
